import type {
  AiPolicy,
  Brief,
  FundingRule,
  Limit,
  OutlineTemplateInput,
  OutletGuidelines,
  OutletKind,
  Output,
  OutputStage,
  OutputType,
  OutputTypeInfo,
  SupplementaryRule,
  TemplateSection,
} from "./types";

// Pure output / brief rules, kept apart from the components so they're tested without a DOM.

export const OUTPUT_TYPE_LABELS: Record<OutputType, string> = {
  abstract: "Abstract",
  manuscript: "Manuscript",
  poster: "Poster",
};

export const OUTPUT_STAGE_LABELS: Record<OutputStage, string> = {
  brief: "Brief",
  first_draft: "First draft",
  senior_qc: "Senior QC",
};

export const OUTLET_KIND_LABELS: Record<OutletKind, string> = {
  journal: "Journal",
  congress: "Congress",
};

export const AI_POLICY_LABELS: Record<AiPolicy, string> = {
  allowed: "Allowed",
  disclosure_required: "Allowed with disclosure",
  not_allowed: "Not allowed",
};

export const FUNDING_LABELS: Record<FundingRule, string> = {
  required: "Required",
  optional: "Optional",
  not_required: "Not required",
};

export const SUPPLEMENTARY_LABELS: Record<SupplementaryRule, string> = {
  allowed: "Allowed",
  not_allowed: "Not allowed",
};

export const outputPath = (projectId: string, outputId: string) => `/projects/${projectId}/outputs/${outputId}`;

/** The supporting document type the Brief's examples/templates are uploaded as. */
export const EXAMPLE_DOCUMENT_TYPE = "example_template";

export const emptyGuidelines = (): OutletGuidelines => ({
  ai_policy: null,
  keywords_max: null,
  funding: null,
  deadline: null,
  audience: null,
  supplementary_material: null,
  title_limit: null,
  body_limit: null,
  graphic_limit: null,
  reference_limit: null,
  required_sections: [],
});

/**
 * A complete guidelines object from whatever the server sent: missing keys get
 * their empty value, so the form never sees `undefined` (an outlet saved before
 * a field existed, or a partial snapshot).
 */
export function normalizeGuidelines(partial?: Partial<OutletGuidelines> | null): OutletGuidelines {
  const base = emptyGuidelines();
  if (!partial) return base;
  const merged = { ...base } as OutletGuidelines;
  for (const key of Object.keys(base) as (keyof OutletGuidelines)[]) {
    const value = partial[key];
    if (value !== undefined) (merged as unknown as Record<string, unknown>)[key] = value;
  }
  merged.required_sections = [...(partial.required_sections ?? [])];
  merged.title_limit = normalizeLimit(partial.title_limit);
  merged.body_limit = normalizeLimit(partial.body_limit);
  return merged;
}

/** Same key order as the form builds, so dirty checks compare like with like. */
const normalizeLimit = (limit: Limit | null | undefined): Limit | null =>
  limit ? { value: limit.value, unit: limit.unit, with_spaces: !!limit.with_spaces } : null;

export const emptyBrief =(): Brief => ({
  objective: "",
  audience: "",
  key_messages: [],
  mandatory_content: "",
  exclusions: "",
  author_client_instructions: "",
  must_use_evidence: "",
  example_document_ids: [],
});

export function normalizeBrief(partial?: Partial<Brief> | null): Brief {
  const base = emptyBrief();
  return {
    objective: partial?.objective ?? base.objective,
    audience: partial?.audience ?? base.audience,
    key_messages: [...(partial?.key_messages ?? [])],
    mandatory_content: partial?.mandatory_content ?? base.mandatory_content,
    exclusions: partial?.exclusions ?? base.exclusions,
    author_client_instructions: partial?.author_client_instructions ?? base.author_client_instructions,
    must_use_evidence: partial?.must_use_evidence ?? base.must_use_evidence,
    example_document_ids: [...(partial?.example_document_ids ?? [])],
  };
}

/** What gets saved: key messages trimmed with blanks dropped (keeping their rank order). */
export function cleanBrief(brief: Brief): Brief {
  return {
    ...brief,
    key_messages: brief.key_messages.map((m) => m.trim()).filter(Boolean),
  };
}

/** A copy with the item at `from` moved to `to`; out-of-range moves return the list unchanged. */
/** A template's sections from an outline's titles (the output type's current outline). */
export function sectionsFromOutline(titles: readonly string[]): TemplateSection[] {
  return titles.map((title) => ({ id: null, title, word_limit: null, instructions: "", subsections: [] }));
}

/** The template as saved: trimmed text, blank subsection lines dropped. */
export function templateBody(value: OutlineTemplateInput): OutlineTemplateInput {
  return {
    ...value,
    name: value.name.trim(),
    description: value.description?.trim() || null,
    sections: value.sections.map((s) => ({
      ...s,
      title: s.title.trim(),
      subsections: s.subsections.map((sub) => sub.trim()).filter(Boolean),
    })),
  };
}

export function moveItem<T>(items: readonly T[], from: number, to: number): T[] {
  const next = [...items];
  if (from < 0 || from >= items.length || to < 0 || to >= items.length || from === to) return next;
  const [item] = next.splice(from, 1);
  next.splice(to, 0, item);
  return next;
}

/** "250-400 words", or "500 words" when the type has no minimum. */
export function wordRangeLabel(range: OutputTypeInfo["word_range"]): string {
  return range.min != null && range.min !== range.max ? `${range.min}-${range.max} words` : `${range.max} words`;
}

/** "300 words", "2,000 characters (with spaces)". */
export function formatLimit(limit: Limit | null): string {
  if (!limit) return "—";
  const value = limit.value.toLocaleString("en-US");
  if (limit.unit === "words") return `${value} words`;
  return `${value} characters (${limit.with_spaces ? "with" : "without"} spaces)`;
}

/** A whole, non-negative number from a number input; empty or invalid text is `null`. */
export function parseCount(text: string): number | null {
  if (text.trim() === "") return null;
  const n = Number(text);
  return Number.isInteger(n) && n >= 0 ? n : null;
}

/**
 * Button labels for a project's outputs: the type ("Abstract"), plus a short
 * title when several outputs share a type so they can be told apart.
 */
export function outputButtonLabels(outputs: Pick<Output, "id" | "type" | "title" | "kantata_task_id" | "kantata_task_title">[]): Map<string, string> {
  const perType = new Map<OutputType, number>();
  for (const o of outputs) perType.set(o.type, (perType.get(o.type) ?? 0) + 1);
  return new Map(
    outputs.map((o) => {
      const typeLabel = OUTPUT_TYPE_LABELS[o.type] ?? o.type;
      if ((perType.get(o.type) ?? 0) < 2) return [o.id, typeLabel];
      const detail = shorten(o.title.trim() || o.kantata_task_title || o.kantata_task_id, 24);
      return [o.id, detail ? `${typeLabel}: ${detail}` : typeLabel];
    }),
  );
}

export function shorten(text: string, max: number): string {
  return text.length <= max ? text : `${text.slice(0, max - 1).trimEnd()}…`;
}

/** Structural equality for the plain JSON values the forms hold (dirty checks). */
export const sameJson = (a: unknown, b: unknown) => JSON.stringify(a) === JSON.stringify(b);

/** Starting text for a Brief nobody has filled in yet; the writer edits or clears it. */
export const SAMPLE_BRIEF: Omit<Brief, "example_document_ids"> = {
  objective:
    "Communicate the 10-year overall survival results of KEYNOTE-006 to establish Keytruda (pembrolizumab) as the long-term standard of care in advanced melanoma.",
  audience: "Medical oncologists and dermato-oncologists treating advanced melanoma",
  key_messages: [
    "Pembrolizumab sustained an overall survival benefit over ipilimumab at 10 years",
    "The safety profile remained consistent with earlier analyses; no new signals",
    "Durable responses persisted after treatment discontinuation",
  ],
  mandatory_content: "Report 10-year OS with HR and 95% CI; include the CONSORT diagram; state the data cut-off date",
  exclusions: "No cross-trial comparisons with nivolumab; no off-label dosing; no unpublished subgroup data",
  author_client_instructions: "The lead author prefers active voice and British spelling; the client requires the trial name in the title",
  must_use_evidence: "KEYNOTE-006 10-year CSR (2024), Robert et al. Lancet Oncol 2019 for the 5-year comparison",
};

/** Nothing written in the Brief's text fields yet (example documents don't count). */
export function isBlankBrief(brief: Brief): boolean {
  const { example_document_ids: _ignored, key_messages, ...text } = cleanBrief(brief);
  return key_messages.length === 0 && Object.values(text).every((v) => !v.trim());
}

/** The Brief to start the form from: the saved one, or the sample text when nothing was written yet. */
export const startingBrief = (brief: Brief): Brief => (isBlankBrief(brief) ? { ...brief, ...SAMPLE_BRIEF, key_messages: [...SAMPLE_BRIEF.key_messages] } : brief);
